import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FlaskConical, Leaf } from 'lucide-react';
import { formatPercentPoints, type Strain } from '@shared';
import { useRevokeObjectUrls } from '../hooks/useRevokeObjectUrls';
import { getProductRepository } from '../lib/productRepository';
import { getProductImagesApi } from '../lib/productImages';
import { revokeDisplayObjectUrls } from '../lib/productImages/objectUrls';
import { ImageUploader } from '../components/ImageUploader';
import { ErrorState, LoadingState } from '../components/ui';
import {
  getRelativeHeroImageId,
  resolveHeroViewerState,
} from './strainHeroViewer';
import {
  buildCannabinoidRows,
  isMeaningfulProductPercent,
  shouldShowSetPrimary,
} from './productDetailsMetrics';

function strainBlobUrls(strain: Strain | null): Array<string | null> {
  return (strain?.images ?? []).flatMap((image) => [
    image.url,
    image.thumbnailUrl,
  ]);
}

/** Full strain record — optional fields omitted when empty; no fabricated data. */
export function StrainDetails(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const [strain, setStrain] = useState<Strain | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failedImageIds, setFailedImageIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [manageImagesOpen, setManageImagesOpen] = useState(false);
  const [editMenuOpen, setEditMenuOpen] = useState(false);
  const mountedRef = useRef(true);
  const manageImagesRef = useRef<HTMLDivElement>(null);
  const editMenuRef = useRef<HTMLDivElement>(null);
  const editMenuButtonRef = useRef<HTMLButtonElement>(null);
  const firstEditActionRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const acceptStrain = useCallback((next: Strain): void => {
    if (!mountedRef.current) {
      revokeDisplayObjectUrls(strainBlobUrls(next));
      return;
    }
    setStrain(next);
  }, []);

  const load = useCallback(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    const products = getProductRepository();
    products
      .get(id)
      .then((s) => {
        if (cancelled || !mountedRef.current) {
          revokeDisplayObjectUrls(strainBlobUrls(s));
          return;
        }
        acceptStrain(s);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Could not load strain.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [acceptStrain, id]);

  useEffect(() => {
    const cancel = load();
    return cancel;
  }, [load]);

  useEffect(() => {
    setSelectedImageId(null);
    setFailedImageIds(new Set());
  }, [id]);

  useEffect(() => {
    if (!editMenuOpen) return;

    const focusFrame = window.requestAnimationFrame(() => {
      firstEditActionRef.current?.focus();
    });
    const closeFromOutside = (event: PointerEvent): void => {
      if (
        event.target instanceof Node &&
        !editMenuRef.current?.contains(event.target)
      ) {
        setEditMenuOpen(false);
      }
    };
    const closeFromEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setEditMenuOpen(false);
      editMenuButtonRef.current?.focus();
    };
    document.addEventListener('pointerdown', closeFromOutside);
    document.addEventListener('keydown', closeFromEscape);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('pointerdown', closeFromOutside);
      document.removeEventListener('keydown', closeFromEscape);
    };
  }, [editMenuOpen]);

  useRevokeObjectUrls(strainBlobUrls(strain));

  const setPrimary = async (imageId: string): Promise<void> => {
    setBusy(true);
    try {
      const updated = await getProductImagesApi().setPrimary(imageId);
      setSelectedImageId(null);
      acceptStrain(updated);
    } finally {
      setBusy(false);
    }
  };

  const deleteImage = async (imageId: string): Promise<void> => {
    if (!window.confirm('Delete this image? This cannot be undone.')) return;
    setBusy(true);
    try {
      await getProductImagesApi().remove(imageId);
      load();
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (): Promise<void> => {
    if (!strain) return;
    const ok = window.confirm(
      'Permanently delete this strain and its images? This cannot be undone.',
    );
    if (!ok) return;
    setBusy(true);
    try {
      await getProductRepository().remove(strain.id);
      navigate('/catalogue');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingState label="Loading strain…" />;
  if (error) return <ErrorState message={error} />;
  if (!strain) return <p className="muted">Not found.</p>;

  const productName = strain.productName ?? strain.name ?? 'Untitled product';
  const brandName = strain.brand ?? strain.producer;
  const title = brandName ? `${brandName} ${productName}` : productName;

  const heroViewer = resolveHeroViewerState(
    strain.images,
    strain.primaryImage?.id ?? null,
    selectedImageId,
    failedImageIds,
  );
  const heroImages = heroViewer.images;
  const heroIndex = heroViewer.index;
  const heroImage = heroViewer.image;
  const hasMultipleHeroImages = heroViewer.hasMultiple;

  const selectRelativeHero = (offset: number): void => {
    const nextImageId = getRelativeHeroImageId(
      heroImages,
      heroIndex,
      offset,
    );
    if (nextImageId) {
      setSelectedImageId(nextImageId);
    }
  };

  const classLabel =
    strain.strainClassSourceText ?? strain.cultivarType ?? strain.category;
  const cannabinoidRows = buildCannabinoidRows([
    { label: 'THC', value: strain.thcPercent },
    { label: 'CBD', value: strain.cbdPercent },
    { label: 'CBG', value: strain.cbgPercent },
    { label: 'CBN', value: strain.cbnPercent },
    { label: 'THCV', value: strain.thcvPercent },
    { label: 'CBC', value: strain.cbcPercent },
  ]);
  const terpeneCompounds = getTerpeneCompounds(strain);
  const secondaryInfo = [
    { label: 'Lineage', value: strain.lineageSourceText },
    { label: 'Terpene compounds', value: terpeneCompounds },
  ];
  const manageableImages = strain.images.filter(
    (image) => image.source !== 'spreadsheet',
  );

  const openManageImages = (): void => {
    setEditMenuOpen(false);
    setManageImagesOpen(true);
    window.requestAnimationFrame(() => {
      manageImagesRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  return (
    <div className="stack details-page">
      <div className="spread">
        <Link to="/catalogue" className="btn" style={{ gap: '0.4rem' }}>
          <ArrowLeft size={18} strokeWidth={2} aria-hidden />
          Library
        </Link>
        <div className="details-edit-menu" ref={editMenuRef}>
          <button
            type="button"
            className="btn details-edit-menu__trigger"
            aria-label="Edit product actions"
            aria-haspopup="menu"
            aria-expanded={editMenuOpen}
            aria-controls="product-edit-menu"
            ref={editMenuButtonRef}
            onClick={() => setEditMenuOpen((open) => !open)}
          >
            Edit
          </button>
          {editMenuOpen ? (
            <div
              className="details-edit-menu__popover"
              id="product-edit-menu"
              role="menu"
              aria-label="Product editing actions"
            >
              <Link
                to={`/strains/${strain.id}/edit`}
                className="details-edit-menu__item"
                role="menuitem"
                ref={firstEditActionRef}
                onClick={() => setEditMenuOpen(false)}
              >
                Edit Info
              </Link>
              <button
                type="button"
                className="details-edit-menu__item"
                role="menuitem"
                onClick={openManageImages}
              >
                Manage Images
              </button>
              <button
                type="button"
                className="details-edit-menu__item details-edit-menu__item--danger"
                role="menuitem"
                onClick={() => {
                  setEditMenuOpen(false);
                  void doDelete();
                }}
                disabled={busy}
              >
                Delete
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="details-identity">
        <h1 className="details-identity__brand">
          {brandName ?? productName}
        </h1>
        {brandName && productName !== brandName ? (
          <div className="details-identity__product">{productName}</div>
        ) : null}
      </div>

      <div className="details-product-stack">
        <div className="details-hero">
          {heroImage ? (
            <>
              <img
                src={heroImage.url}
                alt={`${title} image ${heroIndex + 1} of ${heroImages.length}`}
                onError={() => {
                  setFailedImageIds((prev) => {
                    if (prev.has(heroImage.id)) return prev;
                    const next = new Set(prev);
                    next.add(heroImage.id);
                    return next;
                  });
                }}
              />
              {hasMultipleHeroImages ? (
                <>
                  <button
                    type="button"
                    className="details-hero__nav details-hero__nav--previous"
                    aria-label="Previous product image"
                    onClick={() => selectRelativeHero(-1)}
                  >
                    <span aria-hidden>‹</span>
                  </button>
                  <button
                    type="button"
                    className="details-hero__nav details-hero__nav--next"
                    aria-label="Next product image"
                    onClick={() => selectRelativeHero(1)}
                  >
                    <span aria-hidden>›</span>
                  </button>
                  <div className="details-hero__count" aria-live="polite">
                    {heroIndex + 1} / {heroImages.length}
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <span className="details-hero__placeholder">
              <Leaf size={48} strokeWidth={1.5} />
            </span>
          )}
        </div>

        <div className="details-high-value">
          {classLabel ? (
            <Metric
              label="Strain classification"
              value={classLabel}
              variant="classification"
            />
          ) : null}
          <div className="details-cannabinoids">
            {cannabinoidRows.map((row, rowIndex) => (
              <div
                className={`details-metric-row ${
                  row.length === 1
                    ? 'details-metric-row--full'
                    : 'details-metric-row--pair'
                }`}
                key={row.map((metric) => metric.label).join('-')}
                data-cannabinoid-row={rowIndex + 1}
              >
                {row.map((metric) => (
                  <Metric
                    key={metric.label}
                    label={metric.label}
                    value={formatPercentPoints(metric.value)}
                  />
                ))}
              </div>
            ))}
          </div>
          {hasMeaningfulTerpeneTotal(strain.totalTerpenesPercent) ? (
            <Metric
              label="Terpene total"
              value={formatPercentPoints(strain.totalTerpenesPercent)}
              variant="terpene"
            />
          ) : null}
          {secondaryInfo.map((item) => (
            <SecondaryInfoRow
              key={item.label}
              label={item.label}
              value={item.value}
            />
          ))}
        </div>

        <div className="card stack details-actions">
          <Link
            to={`/calculator?strainA=${strain.id}`}
            className="btn btn-primary btn-lg btn-block"
          >
            <FlaskConical size={18} strokeWidth={2} aria-hidden />
            Use in Calculator
          </Link>
        </div>
      </div>

      {strain.notes ? (
        <section className="card stack" style={{ gap: '0.5rem' }}>
          <h2 className="calc-step__title" style={{ margin: 0 }}>
            About
          </h2>
          <p className="muted" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
            {strain.notes}
          </p>
        </section>
      ) : null}

      {manageImagesOpen ? (
        <div className="card stack" id="manage-images" ref={manageImagesRef}>
          <div className="spread">
            <h2 className="mt-0" style={{ marginBottom: 0 }}>
              Manage Images
            </h2>
            <button
              type="button"
              className="btn"
              onClick={() => setManageImagesOpen(false)}
            >
              Done
            </button>
          </div>
          <ImageUploader
            strainId={strain.id}
            onUploaded={(updated) => {
              setSelectedImageId(null);
              acceptStrain(updated);
            }}
          />
          {manageableImages.length > 0 ? (
            <div className="manage-images__grid">
              {manageableImages.map((image) => {
                const isPrimary =
                  image.isPrimary || strain.primaryImage?.id === image.id;
                return (
                  <article className="manage-image" key={image.id}>
                    <img
                      src={image.thumbnailUrl ?? image.url}
                      alt={`${title} package`}
                    />
                    <div className="manage-image__body">
                      <div
                        className="row"
                        style={{ justifyContent: 'space-between' }}
                      >
                        <strong>
                          {isPrimary ? 'Primary' : 'Package image'}
                        </strong>
                      </div>
                      <div
                        className="row"
                        style={{ flexWrap: 'wrap', gap: '0.4rem' }}
                      >
                        {shouldShowSetPrimary(
                          isPrimary,
                          manageableImages.length,
                        ) ? (
                          <button
                            type="button"
                            className="btn"
                            onClick={() => void setPrimary(image.id)}
                            disabled={busy}
                          >
                            Set Primary
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => void deleteImage(image.id)}
                          disabled={busy}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="muted" style={{ margin: 0 }}>
              No editable package images yet.
            </p>
          )}
        </div>
      ) : null}

    </div>
  );
}

function hasMeaningfulTerpeneTotal(
  value: number | null | undefined,
): value is number {
  return isMeaningfulProductPercent(value) && value > 0;
}

function Metric({
  label,
  value,
  variant,
}: {
  label: string;
  value: string;
  variant?: 'classification' | 'terpene';
}): JSX.Element {
  return (
    <div
      className={`details-metric${
        variant ? ` details-metric--${variant}` : ''
      }`}
    >
      <div className="details-metric__label">{label}</div>
      <div className="details-metric__value">{value}</div>
    </div>
  );
}

function getTerpeneCompounds(strain: Strain): string | null {
  const candidate = (strain as Strain & { terpeneCompounds?: unknown })
    .terpeneCompounds;
  if (typeof candidate === 'string') return candidate.trim() || null;
  if (Array.isArray(candidate)) {
    const compounds = candidate.filter(
      (item): item is string => typeof item === 'string' && item.trim() !== '',
    );
    return compounds.length > 0
      ? compounds.map((item) => item.trim()).join(', ')
      : null;
  }
  return null;
}

function SecondaryInfoRow({
  label,
  value,
}: {
  label: string;
  value: string | null;
}): JSX.Element | null {
  if (!value?.trim()) return null;
  return (
    <div className="secondary-info__row">
      <div className="details-metric__label">{label}</div>
      <div className="secondary-info__value">{value}</div>
    </div>
  );
}
